/**
 * Line readout under the preview (W2-02, W3-06; DESIGN.md §4.8, §5.6.6, RPE TAB overlay VIEW-07; Shift+I toggles): the
 * active line's X, Y, rotation, opacity and speed at the playhead ("Speed 10 (crosses in 0.75 s)"), updated through
 * refs at 15 Hz (no React state per frame). In Edit mode with the gizmo the opacity value is a small slider: dragging
 * it runs the key rule like the other gizmo handles (one undo step on release).
 * The readout is one row at every panel width (UX-051): in a narrow preview column the labels shorten ("Rot", "Op";
 * the full names stay for screen readers), the crossing time moves into the speed's tooltip and the line name
 * truncates.
 */

import * as RSl from '@radix-ui/react-slider';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Units } from '../../core/model/units';
import type { LineId } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import { useEditorValue } from './hooks';
import { PropertyEdit } from './overlays/gizmo';
import { tidyValue } from './overlays/gizmoMath';
import { lineLabel } from './overlays/shared';
import { m } from './preview.messages';
import s from './Preview.module.css';

/** Readout refresh interval (15 Hz). */
const READOUT_MS = 66;

export interface ReadoutValues {
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly rot: number;
  /** Opacity in percent (Σ alpha / 2.55), NaN when absent. */
  readonly opacity: number;
  readonly speed: number;
}

/** Values of a line at the playhead (display values: Σ over layers, RPE units). */
export function readoutValues(services: AppServices, line: LineId): ReadoutValues | null {
  const { store, engine } = services;
  const rec = store?.doc.lines.get(line);
  if (store === null || engine === null || rec === undefined) return null;
  const t = services.audio.transport.chartTime();
  const v = (p: Parameters<typeof engine.channelValue>[1]): number => engine.channelValue(line, p, t, 'sum');
  const alpha = v('alpha');
  return {
    label: lineLabel(store.doc.lineOrder.indexOf(line), rec.name),
    x: v('moveX'),
    y: v('moveY'),
    rot: v('rotate'),
    opacity: Number.isFinite(alpha) ? alpha / 2.55 : Number.NaN,
    speed: v('speed'),
  };
}

function num(v: number, digits = 1): string {
  if (!Number.isFinite(v)) return '-';
  return String(tidyValue(v, digits));
}

export function Readout(props: { readonly services: AppServices }) {
  const { services } = props;
  const t = useT();
  const line = useEditorValue(services, (st) => st.activeLine);
  const sliderOn = useEditorValue(services, (st) => st.preview.gizmo && st.preview.mode === 'edit');
  const nameRef = useRef<HTMLSpanElement>(null);
  const xRef = useRef<HTMLSpanElement>(null);
  const yRef = useRef<HTMLSpanElement>(null);
  const rotRef = useRef<HTMLSpanElement>(null);
  const alphaRef = useRef<HTMLSpanElement>(null);
  const speedRef = useRef<HTMLSpanElement>(null);
  const crossRef = useRef<HTMLSpanElement>(null);
  const speedItemRef = useRef<HTMLSpanElement>(null);
  const [opacity, setOpacity] = useState<number | null>(null);
  const edit = useRef<{ readonly edit: PropertyEdit; readonly startPct: number } | null>(null);

  useEffect(() => {
    if (line === null) return undefined;
    const tick = (): void => {
      const v = readoutValues(services, line);
      if (v === null) return;
      const set = (r: React.RefObject<HTMLSpanElement | null>, text: string): void => {
        if (r.current !== null && r.current.textContent !== text) r.current.textContent = text;
      };
      set(nameRef, v.label);
      set(xRef, num(v.x));
      set(yRef, num(v.y));
      set(rotRef, t(m.readoutDeg, { value: num(v.rot) }));
      set(alphaRef, Number.isFinite(v.opacity) ? t(m.percentValue, { value: num(v.opacity, 0) }) : t(m.readoutAbsent));
      const cross = Units.crossScreenSec(v.speed);
      const crossText = Number.isFinite(cross) ? t(m.readoutCross, { sec: num(cross, 2) }) : t(m.readoutNeverCrosses);
      set(speedRef, num(v.speed, 2));
      set(crossRef, crossText);
      const item = speedItemRef.current;
      if (item !== null && item.title !== crossText) item.title = crossText;
      if (edit.current === null) {
        const pct = Number.isFinite(v.opacity) ? Math.round(v.opacity) : 0;
        setOpacity((prev) => (prev === pct ? prev : pct));
      }
    };
    const first = setTimeout(tick, 0);
    const id = setInterval(tick, READOUT_MS);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, [services, line, t]);

  if (line === null) {
    return (
      <div className={s.readout} role="status" aria-label={t(m.readoutLabel)}>
        {t(m.readoutNoLine)}
      </div>
    );
  }

  const onOpacity = (v: number, final: boolean): void => {
    if (edit.current === null) {
      const e = PropertyEdit.begin(services, line, ['alpha'], m.txnOpacity, false);
      if (e === null) return;
      edit.current = { edit: e, startPct: opacity ?? v };
    }
    const cur = edit.current;
    const start = cur.edit.starts.get('alpha') ?? 0;
    const target = Math.round(start + (v - cur.startPct) * 2.55);
    cur.edit.update(new Map([['alpha', target]]), { offset: false });
    setOpacity(v);
    if (final) {
      const reason = cur.edit.commit();
      edit.current = null;
      if (reason !== null) services.ui.toast(reason, { kind: 'error' });
    }
  };

  /** A value with its label: the full label, or the short one in a narrow panel (full one kept for readers). */
  const item = (long: string, short: string | null, value: ReactNode, extra?: ReactNode) => (
    <span className={s.readoutItem}>
      <span className={s.readoutKey}>
        {short === null ? (
          long
        ) : (
          <>
            <span className={s.readoutKeyLong}>{long}</span>
            <span className={s.readoutKeyShort} aria-hidden>
              {short}
            </span>
          </>
        )}
      </span>
      {value}
      {extra}
    </span>
  );

  return (
    <div className={s.readout} aria-label={t(m.readoutLabel)} data-help-id="preview-readout">
      <span ref={nameRef} className={s.readoutName} />
      {item(t(m.propX), null, <span ref={xRef} />)}
      {item(t(m.propY), null, <span ref={yRef} />)}
      {item(t(m.propRotate), t(m.propRotateShort), <span ref={rotRef} />)}
      {item(
        t(m.propOpacity),
        t(m.propOpacityShort),
        sliderOn && opacity !== null ? (
          <RSl.Root
            className={s.miniSlider}
            value={[opacity]}
            min={0}
            max={100}
            step={1}
            onValueChange={(v) => {
              const x = v[0];
              if (x !== undefined) onOpacity(x, false);
            }}
            onValueCommit={(v) => {
              // Radix reports a keyboard commit before the matching change; deliver the final value after it.
              const x = v[0];
              if (x !== undefined) {
                queueMicrotask(() => {
                  onOpacity(x, true);
                });
              }
            }}
          >
            <RSl.Track className={s.miniTrack}>
              <RSl.Range className={s.miniRange} />
            </RSl.Track>
            <RSl.Thumb
              className={s.miniThumb}
              aria-label={t(m.opacitySlider)}
              aria-valuetext={t(m.percentValue, { value: opacity })}
              data-testid="readout-opacity"
            />
          </RSl.Root>
        ) : null,
        <span ref={alphaRef} />,
      )}
      <span ref={speedItemRef} className={s.readoutItem}>
        <span className={s.readoutKey}>
          <span className={s.readoutKeyLong}>{t(m.propSpeed)}</span>
          <span className={s.readoutKeyShort} aria-hidden>
            {t(m.propSpeedShort)}
          </span>
        </span>
        <span ref={speedRef} />
        <span ref={crossRef} className={s.readoutExtra} />
      </span>
    </div>
  );
}
